import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  DescriptionTable,
  Forbidden,
  HiddenPasswordField,
  JsonView,
  NotFound,
  Notification,
  Page,
  Tab,
  TabContent,
  Tabs,
  TabsHeader,
} from 'shared/components';
import { displayValue, hasPermission, isFeatureEnabled } from 'shared/helpers';
import { fetchLicenseUser } from 'licenseUsers/actions';
import LicenseUserLicenses from './LicenseUserLicenses';
import LicenseUserDevices from './LicenseUserDevices';
import LicenseUserGroups from './LicenseUserGroups';
import EditLicenseUserModal from './EditLicenseUserModal';
import './styles.scss';

const LicenseUserDetails = () => {
  const { t } = useTranslation();
  const { licenseUserId } = useParams();
  const permissionsList = useSelector((state) =>
    get(state, 'user.details.permissions'),
  );
  const canManageLicenseUsers = hasPermission(
    permissionsList,
    'manage_license_users',
  );
  const canUseUserGroups = isFeatureEnabled('usage:user-groups');

  const [user, setUser] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [isEditModalDisplayed, setEditModalDisplay] = useState(false);

  useEffect(() => {
    if (!canManageLicenseUsers) {
      return;
    }

    let isMounted = true;
    setLoading(true);
    setNotFound(false);
    setHasError(false);

    fetchLicenseUser(licenseUserId)
      .then((res) => {
        if (!isMounted) return;
        setUser(get(res, 'data') || null);
        setLoading(false);
      })
      .catch((error) => {
        if (!isMounted) return;
        const status = get(error, 'response.status');
        if (status === 404) {
          setNotFound(true);
        } else {
          setHasError(true);
          Notification(
            'error',
            t('Error occured'),
            t('There was an error while getting license user details'),
          );
        }
        setUser(null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [canManageLicenseUsers, licenseUserId, retryToken, t]);

  const handleRetry = () => setRetryToken((token) => token + 1);
  const handleEditSuccess = () => {
    setEditModalDisplay(false);
    setRetryToken((token) => token + 1);
  };

  if (!canManageLicenseUsers) {
    return (
      <Page>
        <Forbidden
          text={t('Missing permission')}
          description={t('You do not have permission to perform this action.')}
        />
      </Page>
    );
  }

  const breadcrumb = (
    <NavLink to="/license_users">{t('License users')}</NavLink>
  );

  if (isLoading && !user) {
    return (
      <Page title={t('License user')} breadcrumb={breadcrumb}>
        <ContentLoader text={t('Getting license user details')} />
      </Page>
    );
  }

  if ((notFound || hasError) && !user) {
    return (
      <Page title={t('License user')} breadcrumb={breadcrumb}>
        {notFound ? (
          <NotFound />
        ) : (
          <div className="LicenseUserDetails-error">
            <p>{t('License user details could not be loaded.')}</p>
            <Button theme="info" size="md" onClick={handleRetry}>
              {t('Retry')}
            </Button>
          </div>
        )}
      </Page>
    );
  }

  return (
    <Page
      title={displayValue(get(user, 'true_email'), t('License user'))}
      breadcrumb={breadcrumb}
    >
      <div className="LicenseUserDetails">
        <div className="LicenseUserDetails-actions">
          <Button
            theme="info"
            size="md"
            onClick={() => setEditModalDisplay(true)}
          >
            {t('Edit license user')}
          </Button>
        </div>
        <DescriptionTable
          details={[
            {
              label: t('Email'),
              value: displayValue(get(user, 'true_email')),
            },
            {
              label: t('First Name'),
              value: displayValue(get(user, 'first_name')),
            },
            {
              label: t('Last Name'),
              value: displayValue(get(user, 'last_name')),
            },
            {
              label: t('Phone number'),
              value: displayValue(get(user, 'phone_number')),
            },
            {
              label: t('Initial password'),
              value: (
                <HiddenPasswordField
                  value={get(user, 'initial_password')}
                  fallback={t('N/A')}
                />
              ),
            },
            {
              label: t('Last login lockout period'),
              value: displayValue(get(user, 'last_login_lockout_period')),
            },
            {
              label: t('Metadata'),
              value: <JsonView value={get(user, 'metadata')} name="metadata" />,
            },
          ]}
        />
        <div className="LicenseUserDetails-tabs">
          <Tabs>
            <TabsHeader>
              <Tab>{t('Licenses')}</Tab>
              <Tab>{t('Devices')}</Tab>
              {canUseUserGroups && <Tab>{t('User groups')}</Tab>}
            </TabsHeader>
            <TabContent>
              <LicenseUserLicenses licenseUserId={licenseUserId} />
            </TabContent>
            <TabContent>
              <LicenseUserDevices licenseUserId={licenseUserId} />
            </TabContent>
            {canUseUserGroups && (
              <TabContent>
                <LicenseUserGroups licenseUserId={licenseUserId} />
              </TabContent>
            )}
          </Tabs>
        </div>
        {isEditModalDisplayed && (
          <EditLicenseUserModal
            user={user}
            userID={licenseUserId}
            closeCb={() => setEditModalDisplay(false)}
            confirmCb={handleEditSuccess}
          />
        )}
      </div>
    </Page>
  );
};

export default LicenseUserDetails;
